import { ATCBActionEventConfig } from 'add-to-calendar-button';
import { addMinutes, formatISO } from 'date-fns';
import { useBooking } from 'hooks';
import { formatDate } from 'utils/date';
import { getHyphenatedDepartmentAddress } from 'utils/department';

interface CalendarEntry {
    calendarButtonConfig: ATCBActionEventConfig | null;
    googleCalendarLink: string | null;
}

const useCalendarEntry = (): CalendarEntry => {
    const { booking } = useBooking();
    const { reason, departmentAndRelatedDepartments, appointment } = booking;
    const department = departmentAndRelatedDepartments.find(({ departmentid }) => departmentid === appointment?.departmentid);

    if (!appointment || !department || !reason) {
        return { calendarButtonConfig: null, googleCalendarLink: null };
    }

    const description = `${reason.name ?? ''}<br />
    Date: ${appointment.date ?? ''}<br />
    Time: ${appointment.displayStartTime ?? ''}<br />
    Location: ${department && getHyphenatedDepartmentAddress(department)}<br />
    Phone: ${department.phone ?? ''}`;

    // Create the config object to pass into our custom buttons.
    // https://add-to-calendar-button.com/advanced-use#case-10
    // This config object will create an ICS file for a calendar entry. i.e. Apple Mail and MS Outlook, all email clients support this file.
    const calendarButtonConfig = {
        name: reason.name ?? '',
        description: description,
        startDate: formatDate(appointment.date, 'iso') ?? '',
        startTime: appointment.starttime ?? '',
        endTime: formatDate(addMinutes(appointment.dateTimeParsed, appointment.duration), 'timeTwentyFourHour') ?? '',
        timeZone: department.timezonename ?? '',
        location: getHyphenatedDepartmentAddress(department),
        trigger: 'click',
        iCalFileName: `Village Medical - ${reason.name ?? ''}`,
    };

    // For 'Google Gmail support, we will link to a url (with querystring values) where they can add this calendar entry to their google calendar.
    // Based on the ISO 8601 standard.
    // https://date-fns.org/v3.3.1/docs/formatISO
    // https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md#dates
    const startTimeForGoogleCalendar = formatISO(appointment.dateTimeParsed, { format: 'basic' });
    const endTimeForGoogleCalendar = formatISO(addMinutes(appointment.dateTimeParsed, appointment.duration), {
        format: 'basic',
    });

    const googleCalendarLink = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${
        reason.name ?? ''
    }&details=${description}&dates=${startTimeForGoogleCalendar}/${endTimeForGoogleCalendar}&ctz=${
        department.timezonename ?? ''
    }`;

    return { calendarButtonConfig, googleCalendarLink };
};

export { useCalendarEntry };
